import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  LOAD_AVAL_ADDRESS,
  loadAvalAddressByZipCode
} from 'src/store/actions/avalBuroFormActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import theme from 'src/theme';
import useStyles from '../styles';
import makeInitialState from './initialState';
import validationSchema from './schema';

const StepTwo = ({ data, onNext, onBack }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const isLoadingAddress = useSelector(
    isLoadingSelector([LOAD_AVAL_ADDRESS.action])
  );

  const [addressData, setAddressData] = useState({
    cities: [],
    colonias: [],
    municipalities: [],
    states: [],
    isValid: null
  });

  useEffect(() => {
    if (data && data.zipCode) {
      dispatch(loadAvalAddressByZipCode(data.zipCode, setAddressData));
    }
  }, [data, dispatch]);

  const handleZipCodeChange = useCallback(
    (e, setFieldValue) => {
      const { value } = e.target;
      setFieldValue('neighborhood', '');
      setFieldValue('city', '');
      setFieldValue('state', '');
      setFieldValue('municipality', '');
      setAddressData({
        cities: [],
        colonias: [],
        municipalities: [],
        states: [],
        isValid: null
      });
      if (value.length === 5) {
        dispatch(loadAvalAddressByZipCode(value, setAddressData));
      }
    },
    [dispatch]
  );

  const handleSubmit = useCallback(values => onNext(values), [onNext]);

  const autocompleteField = useCallback(
    ({ name, label, options, values, touched, errors, setFieldValue, disabled }) => (
      <FormControl fullWidth key={name}>
        <Autocomplete
          key={`${name}-${values.zipCode}`}
          options={options.map(o => ({ key: o, name: o }))}
          getOptionLabel={option => option?.name ?? ''}
          onChange={(_, value) => setFieldValue(name, value ? value.name : '')}
          value={
            options.map(o => ({ key: o, name: o })).find(o => o.name === values[name]) ?? {
              key: values[name] ?? '',
              name: values[name] ?? ''
            }
          }
          loading={isLoadingAddress}
          clearOnBlur
          selectOnFocus={false}
          handleHomeEndKeys={false}
          freeSolo
          inputValue={values[name] ?? ''}
          onInputChange={(_, value) => setFieldValue(name, value)}
          sx={{ marginBottom: 0, marginTop: 2 }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              fullWidth
              disabled={disabled}
              error={Boolean(touched[name] && errors[name])}
              helperText={touched[name] && t(errors[name])}
            />
          )}
          loadingText={t('COMMON.LOADING')}
        />
      </FormControl>
    ),
    [isLoadingAddress, t]
  );

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, handleSubmit: formikSubmit, touched, values, handleBlur, handleChange, setFieldValue }) => {
        const zipNotFound = addressData.isValid === false && values.zipCode?.length === 5;
        const dependentDisabled = !values.zipCode || isLoadingAddress;

        return (
          <form onSubmit={formikSubmit}>
            <Box>
              <Typography
                variant="h3"
                sx={{ marginBottom: '16px', color: theme.palette.primary.main }}
              >
                {t('AVAL_BURO_FORM.STEP_TWO.TITLE')}
              </Typography>

              <TextField
                error={Boolean(touched.address && errors.address)}
                fullWidth
                helperText={touched.address && t(errors.address)}
                label={t('AVAL_BURO_FORM.STEP_TWO.ADDRESS')}
                margin="normal"
                name="address"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.address}
                variant="outlined"
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(touched.exteriorNumber && errors.exteriorNumber)}
                    fullWidth
                    helperText={touched.exteriorNumber && t(errors.exteriorNumber)}
                    label={t('AVAL_BURO_FORM.STEP_TWO.EXTERIOR_NUMBER')}
                    margin="normal"
                    name="exteriorNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.exteriorNumber}
                    variant="outlined"
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(touched.interiorNumber && errors.interiorNumber)}
                    fullWidth
                    helperText={touched.interiorNumber && t(errors.interiorNumber)}
                    label={t('AVAL_BURO_FORM.STEP_TWO.INTERIOR_NUMBER')}
                    margin="normal"
                    name="interiorNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.interiorNumber}
                    variant="outlined"
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
              </Grid>

              <TextField
                error={Boolean((touched.zipCode && errors.zipCode) || zipNotFound)}
                fullWidth
                helperText={
                  zipNotFound
                    ? t('ERRORS.ZIP_CODE_WAS_NOT_FOUND')
                    : touched.zipCode && t(errors.zipCode)
                }
                label={t('AVAL_BURO_FORM.STEP_TWO.ZIP_CODE')}
                margin="normal"
                name="zipCode"
                onBlur={handleBlur}
                onChange={e => {
                  handleZipCodeChange(e, setFieldValue);
                  handleChange(e);
                }}
                value={values.zipCode}
                variant="outlined"
                InputProps={{
                  inputProps: { maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*' },
                  endAdornment: isLoadingAddress ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }}
              />

              {autocompleteField({
                name: 'neighborhood',
                label: t('AVAL_BURO_FORM.STEP_TWO.NEIGHBORHOOD'),
                options: addressData.colonias ?? [],
                values,
                touched,
                errors,
                setFieldValue,
                disabled: dependentDisabled
              })}

              {autocompleteField({
                name: 'municipality',
                label: t('AVAL_BURO_FORM.STEP_TWO.MUNICIPALITY'),
                options: addressData.municipalities ?? [],
                values,
                touched,
                errors,
                setFieldValue,
                disabled: dependentDisabled
              })}

              {autocompleteField({
                name: 'city',
                label: t('AVAL_BURO_FORM.STEP_TWO.CITY'),
                options: addressData.cities ?? [],
                values,
                touched,
                errors,
                setFieldValue,
                disabled: dependentDisabled
              })}

              {autocompleteField({
                name: 'state',
                label: t('AVAL_BURO_FORM.STEP_TWO.STATE'),
                options: addressData.states ?? [],
                values,
                touched,
                errors,
                setFieldValue,
                disabled: dependentDisabled
              })}
            </Box>

            <Box className={classes.box}>
              <Button
                className={classes.buttons}
                onClick={onBack}
                color="primary"
                variant="outlined"
              >
                {t('COMMON.BACK')}
              </Button>
              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="contained"
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

StepTwo.propTypes = {
  data: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default StepTwo;
